import React from 'react';
import logoBlanco from './centrosurablanco.png';

const bannerStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;600;800&family=Space+Grotesk:wght@300;500;700&display=swap');

  :root {
    --bg-deep: #020617;
    --bg-glow: #0f172a;
    --accent-blue: #38bdf8;
    --accent-cyan: #22d3ee;
    --text-main: #f8fafc;
    --text-muted: #94a3b8;
  }

  /* Complex layered background */
  .cosmic-void {
    background-color: var(--bg-deep);
    background-image: 
      radial-gradient(circle at 50% 0%, rgba(14, 165, 233, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(56, 189, 248, 0.1) 0%, transparent 40%),
      url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E");
    background-size: 100% 100%, 100% 100%, 150px 150px;
  }

  /* Ethereal light beams */
  .light-beam {
    position: absolute;
    top: -50%;
    left: 50%;
    width: 1px;
    height: 200%;
    background: linear-gradient(to bottom, 
      rgba(255,255,255,0) 0%,
      rgba(56, 189, 248, 0.1) 40%,
      rgba(255,255,255,0.4) 50%,
      rgba(56, 189, 248, 0.1) 60%,
      rgba(255,255,255,0) 100%);
    transform: rotate(45deg);
    animation: sweep 12s infinite linear;
    filter: blur(4px);
  }

  .light-beam:nth-child(2) {
    transform: rotate(-45deg);
    animation-direction: reverse;
    animation-duration: 15s;
    opacity: 0.5;
  }

  @keyframes sweep {
    0% { transform: translateX(-100vw) rotate(45deg); }
    100% { transform: translateX(100vw) rotate(45deg); }
  }

  /* Stunning typography animations */
  .reveal-text {
    background: linear-gradient(to right, #fff, var(--accent-blue), #fff);
    background-size: 200% auto;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    animation: shine 4s linear infinite;
  }

  @keyframes shine {
    to { background-position: 200% center; }
  }

  .glass-card {
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.1);
    position: relative;
    overflow: hidden;
  }
  
  .glass-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(125deg, rgba(255,255,255,0.05) 0%, transparent 40%, transparent 60%, rgba(56, 189, 248, 0.05) 100%);
    pointer-events: none;
  }

  /* Starfield */
  .star {
    position: absolute;
    background: #fff;
    border-radius: 50%;
    animation: twinkle var(--duration, 3s) ease-in-out infinite alternate;
  }

  @keyframes twinkle {
    0% { opacity: 0.1; transform: scale(0.8); }
    100% { opacity: 0.8; transform: scale(1.2); box-shadow: 0 0 10px rgba(255,255,255,0.5); }
  }

  /* Floating animation */
  @keyframes ethereal-float {
    0%, 100% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-15px) scale(1.01); }
  }
  
  .animate-float {
    animation: ethereal-float 8s ease-in-out infinite;
  }

  /* Entrance animations */
  @keyframes fade-up {
    0% { opacity: 0; transform: translateY(30px); filter: blur(10px); }
    100% { opacity: 1; transform: translateY(0); filter: blur(0); }
  }
  
  @keyframes scale-up {
    0% { opacity: 0; transform: scale(0.9); filter: blur(10px); }
    100% { opacity: 1; transform: scale(1); filter: blur(0); }
  }

  .in-up-1 { animation: fade-up 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both; }
  .in-up-2 { animation: fade-up 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both; }
  .in-up-3 { animation: fade-up 1.2s cubic-bezier(0.16, 1, 0.3, 1) 1.0s both; }
  .in-scale { animation: scale-up 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0s both; }

  /* Rocket animations */
  @keyframes rocketFly {
    0% { transform: translateY(8px) rotate(-2deg); }
    25% { transform: translateY(-6px) rotate(1deg); }
    50% { transform: translateY(4px) rotate(-1deg); }
    75% { transform: translateY(-8px) rotate(2deg); }
    100% { transform: translateY(8px) rotate(-2deg); }
  }
  @keyframes flame {
    0%, 100% { transform: scaleY(1) scaleX(1); opacity: 0.9; }
    25% { transform: scaleY(1.3) scaleX(0.8); opacity: 1; }
    50% { transform: scaleY(0.8) scaleX(1.2); opacity: 0.7; }
    75% { transform: scaleY(1.2) scaleX(0.9); opacity: 1; }
  }
  @keyframes smokeTrail {
    0% { opacity: 0.6; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(30px) scale(2); }
  }
  @keyframes orbit {
    0% { transform: rotate(0deg) translateX(60px) rotate(0deg); }
    100% { transform: rotate(360deg) translateX(60px) rotate(-360deg); }
  }
`;

// Deterministic star generation for a deep, dimensional field
const generateStars = (count) => {
  return Array.from({ length: count }).map((_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    size: Math.random() > 0.8 ? `${Math.random() * 2 + 1.5}px` : `${Math.random() * 1 + 0.5}px`,
    duration: `${Math.random() * 3 + 2}s`,
    delay: `-${Math.random() * 5}s`,
    opacity: Math.random() * 0.5 + 0.1
  }));
};

const STARS = generateStars(80);

const ComingSoonLanding = () => {
  return (
    <>
      <style>{bannerStyles}</style>
      
      <div className="relative w-screen h-screen cosmic-void flex flex-col items-center justify-center overflow-hidden font-['Outfit',sans-serif]">
        
        {/* Dynamic Light Beams */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="light-beam" />
          <div className="light-beam" />
        </div>

        {/* Ambient Starfield */}
        <div className="absolute inset-0 z-0">
          {STARS.map((star) => (
            <div
              key={star.id}
              className="star"
              style={{
                top: star.top,
                left: star.left,
                width: star.size,
                height: star.size,
                '--duration': star.duration,
                animationDelay: star.delay,
                opacity: star.opacity
              }}
            />
          ))}
        </div>

        {/* Central Core Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[800px] max-h-[800px] rounded-full bg-blue-500/5 blur-[120px] pointer-events-none z-0 opacity-60" />

        {/* Main Content — centered */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-6 text-center">

          {/* Logo inside glass card */}
          <div className="glass-card rounded-[2.5rem] p-10 md:p-16 mb-12 in-scale animate-float flex flex-col items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-sky-400/20 blur-3xl rounded-full" />
              <img
                src={logoBlanco}
                alt="Centro SURA"
                className="relative z-10 h-24 md:h-36 xl:h-44 w-auto object-contain drop-shadow-[0_0_25px_rgba(255,255,255,0.4)]"
              />
            </div>

            {/* Separator */}
            <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-sky-400/50 to-transparent my-10 in-up-1" />

            {/* Bold Statement */}
            <div className="flex flex-col items-center in-up-2">
              <div className="inline-block relative">
                <span className="absolute -inset-4 bg-sky-500/10 blur-xl rounded-full" />
                <h1 className="relative font-['Space_Grotesk',sans-serif] text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-white mb-2 leading-none reveal-text">
                  FALTAN 3 DÍAS
                </h1>
              </div>

              <div className="flex items-center gap-4 mt-6 opacity-80">
                <div className="w-8 h-[1px] bg-sky-400/60" />
                <p className="text-sky-100 uppercase tracking-[0.3em] text-sm md:text-base font-semibold">
                  El futuro está cargando
                </p>
                <div className="w-8 h-[1px] bg-sky-400/60" />
              </div>
            </div>
          </div>

          {/* Subtext */}
          <div className="in-up-3">
            <p className="text-slate-400 text-lg md:text-2xl font-light tracking-wide max-w-2xl mx-auto leading-relaxed">
              Prepárate para <span className="text-white font-medium">cambiar</span> tu forma de trabajar.
            </p>
          </div>

          {/* Rocket */}
          <div className="relative mt-10 in-up-3" style={{ width: '120px', height: '140px' }}>
            {/* Orbiting particles */}
            <div className="absolute pointer-events-none" style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'rgba(56,189,248,0.6)', left: '50%', top: '50%', animation: 'orbit 6s linear infinite' }} />
            <div className="absolute pointer-events-none" style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.4)', left: '50%', top: '50%', animation: 'orbit 8s linear reverse infinite' }} />

            {/* Rocket SVG */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'rocketFly 4s ease-in-out infinite' }}>
              <svg width="60" height="90" viewBox="0 0 70 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="35" cy="38" rx="14" ry="32" fill="white" />
                <path d="M35 2 L45 28 L25 28 Z" fill="white" />
                <path d="M35 2 L42 22 L35 18 Z" fill="rgba(200,220,240,0.6)" />
                <circle cx="35" cy="32" r="6" fill="#0f172a" stroke="rgba(56,189,248,0.6)" strokeWidth="1.5" />
                <circle cx="33" cy="30" r="2" fill="rgba(56,189,248,0.3)" />
                <path d="M21 55 L10 70 L21 62 Z" fill="white" opacity="0.9" />
                <path d="M49 55 L60 70 L49 62 Z" fill="white" opacity="0.9" />
                <rect x="25" y="48" width="20" height="3" rx="1.5" fill="rgba(56,189,248,0.2)" />
                <g style={{ animation: 'flame 0.3s ease-in-out infinite', transformOrigin: '35px 70px' }}>
                  <ellipse cx="35" cy="76" rx="8" ry="12" fill="rgba(56,189,248,0.7)" />
                  <ellipse cx="35" cy="78" rx="5" ry="10" fill="rgba(125,211,252,0.8)" />
                  <ellipse cx="35" cy="80" rx="3" ry="7" fill="rgba(255,255,255,0.9)" />
                </g>
                <circle cx="30" cy="88" r="3" fill="rgba(255,255,255,0.1)" style={{ animation: 'smokeTrail 1.5s ease-out infinite' }} />
                <circle cx="40" cy="90" r="2.5" fill="rgba(255,255,255,0.08)" style={{ animation: 'smokeTrail 1.5s ease-out 0.3s infinite' }} />
                <circle cx="35" cy="92" r="2" fill="rgba(255,255,255,0.06)" style={{ animation: 'smokeTrail 1.5s ease-out 0.6s infinite' }} />
              </svg>
            </div>
          </div>

        </div>

      </div>
    </>
  );
};

export default ComingSoonLanding;
